import { useEffect, useId, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';

interface UseVacancyFilterComboboxParams {
  isDisabled: boolean;
  options: string[];
  selectedValues: string[];
  onToggle: (value: string) => void;
}

export const useVacancyFilterCombobox = ({
  isDisabled,
  options,
  selectedValues,
  onToggle,
}: UseVacancyFilterComboboxParams) => {
  const [isOpen, setIsOpen] = useState(false);
  const baseId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const optionRefs = useRef<Array<HTMLInputElement | null>>([]);
  const selectedOptionIndex = options.findIndex((option) =>
    selectedValues.includes(option)
  );
  const firstFocusedOptionIndex =
    selectedOptionIndex === -1 ? 0 : selectedOptionIndex;

  useEffect(() => {
    optionRefs.current = optionRefs.current.slice(0, options.length);
  }, [options.length]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const closeOnOutsideInteraction = (event: MouseEvent | FocusEvent) => {
      if (
        event.target instanceof Node &&
        !rootRef.current?.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', closeOnOutsideInteraction);
    document.addEventListener('focusin', closeOnOutsideInteraction);

    return () => {
      document.removeEventListener('mousedown', closeOnOutsideInteraction);
      document.removeEventListener('focusin', closeOnOutsideInteraction);
    };
  }, [isOpen]);

  const focusOption = (index: number) => {
    window.setTimeout(() => {
      optionRefs.current[index]?.focus();
    }, 0);
  };

  const openAndFocusOption = (index: number) => {
    setIsOpen(true);
    focusOption(index);
  };

  const closeAndFocusTrigger = () => {
    setIsOpen(false);
    triggerRef.current?.focus();
  };

  const handleTriggerKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (isDisabled) {
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      closeAndFocusTrigger();
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      openAndFocusOption(firstFocusedOptionIndex);
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      openAndFocusOption(
        selectedOptionIndex === -1 ? options.length - 1 : selectedOptionIndex
      );
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();

      if (isOpen) {
        setIsOpen(false);
      } else {
        openAndFocusOption(firstFocusedOptionIndex);
      }
    }
  };

  const handleOptionKeyDown = (
    event: KeyboardEvent<HTMLInputElement>,
    option: string,
    index: number
  ) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      closeAndFocusTrigger();
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      optionRefs.current[(index + 1) % options.length]?.focus();
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      optionRefs.current[
        (index - 1 + options.length) % options.length
      ]?.focus();
      return;
    }

    if (event.key === 'Home') {
      event.preventDefault();
      optionRefs.current[0]?.focus();
      return;
    }

    if (event.key === 'End') {
      event.preventDefault();
      optionRefs.current[options.length - 1]?.focus();
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      onToggle(option);
    }
  };

  return {
    handleOptionKeyDown,
    handleTriggerKeyDown,
    isOpen,
    listboxId: `${baseId}-listbox`,
    optionRefs,
    rootRef,
    setIsOpen,
    triggerRef,
  };
};
